// -----------------------------------------------------------------------------
// engine/compositor/gl.ts — the WebGL2 compositor.
//
// Owns a WebGL2 context and draws layers as textured quads, with alpha blending
// so upper layers composite over lower ones. It is deliberately low-level: clear
// the frame, then drawTexturedQuad(source, rect, opacity) for each layer. The
// higher-level "draw this clip" logic lives in layer.ts.
//
// One compositor instance renders the live preview (onto a visible canvas); the
// exporter creates a second one at full project resolution (onto an
// OffscreenCanvas). Either way the engine drives it imperatively — the UI never
// reaches in here (CLAUDE.md §4 principle 5).
// -----------------------------------------------------------------------------
import type { Rect } from '../../shared/geometry';
import { QUAD_FRAGMENT_SHADER, QUAD_VERTEX_SHADER } from './shaders/quad';

export type GLCanvas = HTMLCanvasElement | OffscreenCanvas;

export class Compositor {
  readonly canvas: GLCanvas;
  private gl: WebGL2RenderingContext;
  private program: WebGLProgram;
  private vbo: WebGLBuffer;
  private texture: WebGLTexture;
  private aPos: number;
  private aUv: number;
  private uOpacity: WebGLUniformLocation | null;

  // Reusable 2D canvas for rendering text overlays into a texture.
  private overlay?: OffscreenCanvas;
  private overlayCtx?: OffscreenCanvasRenderingContext2D;

  width: number;
  height: number;

  constructor(canvas: GLCanvas, width: number, height: number) {
    this.canvas = canvas;
    this.width = width;
    this.height = height;
    canvas.width = width;
    canvas.height = height;

    // HTMLCanvasElement and OffscreenCanvas have separate (but identical for
    // 'webgl2') getContext overloads, so cast to one shape to call it cleanly.
    const gl = (canvas as HTMLCanvasElement).getContext('webgl2', {
      premultipliedAlpha: false,
      alpha: true,
      preserveDrawingBuffer: true, // so export can read pixels back reliably
    }) as WebGL2RenderingContext | null;
    if (!gl) throw new Error('WebGL2 is not available — the compositor cannot start.');
    this.gl = gl;

    this.program = linkProgram(gl, QUAD_VERTEX_SHADER, QUAD_FRAGMENT_SHADER);
    this.aPos = gl.getAttribLocation(this.program, 'a_pos');
    this.aUv = gl.getAttribLocation(this.program, 'a_uv');
    this.uOpacity = gl.getUniformLocation(this.program, 'u_opacity');

    this.vbo = mustCreate(gl.createBuffer(), 'vertex buffer');
    this.texture = mustCreate(gl.createTexture(), 'texture');

    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    // Our quad maps the image's top row to texture v=0 (see quadVertices), and
    // texImage2D already places the source's top row at v=0 — so NO upload flip,
    // otherwise images/text render upside down.
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  }

  resize(width: number, height: number): void {
    if (width === this.width && height === this.height) return;
    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;
    if (this.overlay) {
      this.overlay.width = width;
      this.overlay.height = height;
    }
  }

  // Clear the frame to a solid background colour (CSS hex like '#000000').
  beginFrame(background: string): void {
    const gl = this.gl;
    gl.viewport(0, 0, this.width, this.height);
    const [r, g, b] = hexToRgb(background);
    gl.clearColor(r, g, b, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  // Draw a texture source into a destination rectangle (pixels, top-left origin).
  drawTexturedQuad(source: TexImageSource, dst: Rect, opacity: number): void {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);

    const verts = this.quadVertices(dst);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, verts, gl.DYNAMIC_DRAW);

    gl.useProgram(this.program);
    gl.enableVertexAttribArray(this.aPos);
    gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(this.aUv);
    gl.vertexAttribPointer(this.aUv, 2, gl.FLOAT, false, 16, 8);
    gl.uniform1f(this.uOpacity, opacity);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  // Build interleaved [x,y,u,v] for two triangles covering the dst rect.
  private quadVertices(dst: Rect): Float32Array {
    const x0 = (dst.x / this.width) * 2 - 1;
    const x1 = ((dst.x + dst.w) / this.width) * 2 - 1;
    const y0 = 1 - (dst.y / this.height) * 2; // top
    const y1 = 1 - ((dst.y + dst.h) / this.height) * 2; // bottom
    // TL, BL, TR, TR, BL, BR  (uv: 0,0 at top-left — combined with FLIP_Y upload)
    return new Float32Array([
      x0, y0, 0, 0,
      x0, y1, 0, 1,
      x1, y0, 1, 0,
      x1, y0, 1, 0,
      x0, y1, 0, 1,
      x1, y1, 1, 1,
    ]);
  }

  // Force pending GL commands to complete — call before capturing the canvas
  // into a VideoFrame during export, so we never grab a half-drawn frame.
  flush(): void {
    this.gl.flush();
    this.gl.finish();
  }

  // Lazily create / return the reusable 2D overlay canvas (for text rendering).
  overlay2d(): { canvas: OffscreenCanvas; ctx: OffscreenCanvasRenderingContext2D } {
    if (!this.overlay || !this.overlayCtx) {
      this.overlay = new OffscreenCanvas(this.width, this.height);
      const ctx = this.overlay.getContext('2d');
      if (!ctx) throw new Error('2D context unavailable for text overlay.');
      this.overlayCtx = ctx;
    }
    return { canvas: this.overlay, ctx: this.overlayCtx };
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteBuffer(this.vbo);
    gl.deleteTexture(this.texture);
    gl.deleteProgram(this.program);
  }
}

function linkProgram(gl: WebGL2RenderingContext, vsSrc: string, fsSrc: string): WebGLProgram {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
  const program = mustCreate(gl.createProgram(), 'program');
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    throw new Error('Shader link failed: ' + log);
  }
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  return program;
}

function compileShader(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const shader = mustCreate(gl.createShader(type), 'shader');
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    throw new Error('Shader compile failed: ' + log);
  }
  return shader;
}

function mustCreate<T>(value: T | null, what: string): T {
  if (value === null) throw new Error(`Failed to create WebGL ${what}.`);
  return value;
}

// '#rrggbb' → normalized [r,g,b] in 0..1. Falls back to black.
function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [0, 0, 0];
  const n = parseInt(m[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}
